import { AppServices, ServicesConfig, ServicesProvider } from '@nab/x-types';
import { History, Action, createBrowserHistory, createMemoryHistory, Location } from 'history';
import { defaultServicesProvider } from './defaultServicesProvider';
import { getShellActions } from '../actions/core';

let shellServices: AppServices;
let servicesProvider: ServicesProvider;

export const setShellServices = (services: AppServices) => {
    shellServices = services;
};

export const getShellServices = (): AppServices => {
    if (typeof shellServices !== 'object') {
        throw new Error(`x-runtime has not been initialised. Please initialise it using the initRuntime function`);
    }
    return shellServices;
};

export const createServices = (servicesConfig?: ServicesConfig): AppServices => {
    let services: AppServices = defaultServicesProvider(servicesConfig);
    if (servicesProvider) {
        services = {
            ...services,
            ...servicesProvider(servicesConfig)
        };
    }
    return services;
};

/**
 * Creates an instance of History for the mini-app to use with it's router
 *
 * The app's History instance will be kept in sync with the shell's History instance thus ensuring that the mini-app
 * can update it's UI & state when the shell changes the URI.
 */
export const createHistoryServiceForMiniApp = (instanceId: string, suppressRouting: boolean = false, routingBasePath: string = ''): History<any> => {
    const basename = `/${routingBasePath.replace(/^\/*/, '').replace(/\/*$/, '')}`;
    const shellHistory = getShellServices().history;
    const appHistory = suppressRouting ? createMemoryHistory() : createBrowserHistory<any>({ basename });
    const cleanUpFns = [];
    let unloaded = false;
    cleanUpFns.push(
        // Sync history state in the app's history instance whenever the shell's history state changes
        shellHistory.listen((location: Location<any>, action: Action) => {
            if (!unloaded && (action === 'PUSH' || action === 'REPLACE') && location.state?.xState?.instanceId === instanceId) {
                appHistory.replace(location.pathname.replace(new RegExp(`^${basename}/?`), '/'), location.state);
            }
        })
    );
    cleanUpFns.push(
        // Unsubscribe the history listener when the mini-app is unloaded to prevent a memory leak
        getShellActions().addEventListener(
            {
                eventName: 'beforeAppUnload',
                appId: 'SHELL'
            },
            (data) => {
                if (data.instanceId === instanceId) {
                    unloaded = true;
                    cleanUpFns.forEach((f) => f());
                }
            }
        )
    );
    return appHistory;
};

export const registerServicesProvider = (provider: ServicesProvider) => {
    servicesProvider = provider;
};

export const getServicesProvider = (): ServicesProvider => servicesProvider;

export const resetServicesRegistry = () => {
    shellServices = undefined;
    servicesProvider = undefined;
};
